import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import {
  PlanType,
  PlanTypesPaginRes,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';
import { urlBackend } from '@/shared/constants/networking.constants';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchPlanTypes = ({
  page = 1,
  nombre = '',
  page_size = 10,
}: getPlanTypesParams) => {
  return useQuery({
    queryKey: ['planTypes', page, nombre],
    queryFn: async () => {
      return getPlanTypes({ page, nombre, page_size });
    },
  });
};

export const useGetPlanType = (id: string) => {
  return useQuery({
    queryKey: ['planTypes', id],
    queryFn: async () => {
      return getPlanType(id);
    },
  });
};

export const useCreatePlanType = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPlanType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planTypes'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdatePlanType = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePlanType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planTypes'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useRemovePlanType = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removePlanType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planTypes'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getPlanTypesParams = {
  page?: number;
  nombre?: string;
  page_size?: number;
};

export type CreatePlanTypeParams = Omit<PlanType, 'id_tipo_plan'>;
export type UpdatePlanTypeParams = {
  id: string;
  data: CreatePlanTypeParams;
};

export const getPlanTypes = async (params?: getPlanTypesParams) => {
  const { page = 1, nombre = '', page_size = 10 } = params || {};

  return get<PlanTypesPaginRes>(
    `${urlBackend}api/v1/tipos-plan/?page=${page}&nombre=${nombre}&page_size=${page_size}`,
    true
  );
};

export const getPlanType = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<PlanType>(`${urlBackend}api/v1/tipos-plan/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createPlanType = async (data: CreatePlanTypeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<PlanType>(`${urlBackend}api/v1/tipos-plan/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updatePlanType = async ({ id, data }: UpdatePlanTypeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<PlanType>(
    `${urlBackend}api/v1/tipos-plan/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const removePlanType = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<PlanType>(
    `${urlBackend}api/v1/tipos-plan/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
